// src/pages/profile/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, getDocs, deleteDoc, addDoc } from 'firebase/firestore';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import FileUpload from '../../components/common/FileUpload';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { updateVendorProfile } from '../../services/authService';
import { db } from '../../services/firebase';

// Helper function to upload UPI scanner image
// You should implement this function in your services/fileUpload.js or similar
const uploadUpiScannerImage = async (file, vendorId) => {
  try {
    // This is a placeholder implementation
    // You should implement actual Firebase Storage upload here
    
    // Example implementation:
    // import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
    // import { storage } from '../services/firebase';
    
    // const storageRef = ref(storage, `vendors/${vendorId}/upi-scanner/${Date.now()}-${file.name}`);
    // const snapshot = await uploadBytes(storageRef, file);
    // const downloadURL = await getDownloadURL(snapshot.ref);
    // return downloadURL;
    
    // For now, return a placeholder or use your existing file upload service
    console.log('Uploading UPI scanner image:', file.name);
    
    // Temporary fallback - you should replace this with actual storage upload
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error('Error uploading UPI scanner image:', error);
    throw error;
  }
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, vendorProfile, updateProfile } = useAuth();
  const { showAlert } = useNotification();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [error, setError] = useState('');
  const [bankError, setBankError] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [isAddingBank, setIsAddingBank] = useState(false);
  const [editingBankId, setEditingBankId] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  // Profile form state
  const [formData, setFormData] = useState({
    shopName: '',
    contactPersonName: '',
    phone: '',
    email: '',
    address: '',
    newLogo: null,
    logoPreview: null
  });

  // Bank details form state
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    bankName: '',
    branchName: '',
    accountType: 'savings',
    upiId: '',
    upiScannerImage: null,
    upiScannerPreview: null,
    isPrimary: false
  });

  // Original bank details for comparison
  const [originalBankDetails, setOriginalBankDetails] = useState({});
  
  // Initialize form data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load profile data
        if (vendorProfile) {
          setFormData({
            shopName: vendorProfile.shopName || '',
            contactPersonName: vendorProfile.contactPersonName || '',
            phone: vendorProfile.phone || '',
            email: vendorProfile.email || currentUser?.email || '',
            address: vendorProfile.address || '',
            newLogo: null,
            logoPreview: vendorProfile.logoURL || null
          });
        }

        // Load bank accounts
        if (currentUser?.uid) {
          const bankCollection = collection(db, 'vendors', currentUser.uid, 'bankAccounts');
          const bankSnapshot = await getDocs(bankCollection);
          const accounts = bankSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setBankAccounts(accounts);
          
          if (accounts.length === 0) {
            setIsAddingBank(true);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vendorProfile, currentUser]);
  
  // Handle profile input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle bank details input change
  const handleBankChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBankDetails(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'ifscCode' ? value.toUpperCase() : value)
    }));
  };
  
  // Handle logo upload
  const handleLogoChange = (file) => {
    setFormData(prev => ({
      ...prev,
      newLogo: file
    }));
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle UPI scanner image upload
  const handleUpiScannerChange = (file) => {
    setBankDetails(prev => ({
      ...prev,
      upiScannerImage: file
    }));
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBankDetails(prev => ({
          ...prev,
          upiScannerPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle profile form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError('');
      
      const profileData = {
        shopName: formData.shopName,
        contactPersonName: formData.contactPersonName,
        phone: formData.phone,
        address: formData.address
      };
      
      // Add new logo if provided
      if (formData.newLogo) {
        profileData.newLogo = formData.newLogo;
      }
      
      await updateProfile(profileData);
      
      showAlert('Profile updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset bank form
  const resetBankForm = () => {
    setBankDetails({
      accountHolderName: '',
      accountNumber: '',
      confirmAccountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: '',
      accountType: 'savings',
      upiId: '',
      upiScannerImage: null,
      upiScannerPreview: null,
      isPrimary: bankAccounts.length === 0
    });
  };

  // Handle add new bank account
  const handleAddBank = () => {
    resetBankForm();
    setIsAddingBank(true);
    setEditingBankId(null);
    setBankError('');
  };

  // Handle edit bank account
  const handleEditBank = (account) => {
    setBankDetails({
      ...account,
      confirmAccountNumber: account.accountNumber,
      upiScannerImage: null,
      upiScannerPreview: account.upiScannerURL || null
    });
    setOriginalBankDetails(account);
    setIsEditingBank(true);
    setEditingBankId(account.id);
    setIsAddingBank(false);
    setBankError('');
  };

  // Handle delete bank account
  const handleDeleteBank = async (accountId) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      try {
        await deleteDoc(doc(db, 'vendors', currentUser.uid, 'bankAccounts', accountId));
        setBankAccounts(prev => prev.filter(acc => acc.id !== accountId));
        showAlert('Bank account deleted successfully!', 'success');
      } catch (error) {
        console.error('Error deleting bank account:', error);
        showAlert('Failed to delete bank account', 'error');
      }
    }
  };

  // Handle set primary account
  const handleSetPrimary = async (accountId) => {
    try {
      // Update all accounts to non-primary
      const updates = bankAccounts.map(async (account) => {
        return setDoc(doc(db, 'vendors', currentUser.uid, 'bankAccounts', account.id), {
          ...account,
          isPrimary: account.id === accountId,
          updatedAt: new Date()
        });
      });
      
      await Promise.all(updates);
      
      // Update local state
      setBankAccounts(prev => prev.map(acc => ({
        ...acc,
        isPrimary: acc.id === accountId
      })));
      
      showAlert('Primary account updated successfully!', 'success');
    } catch (error) {
      console.error('Error setting primary account:', error);
      showAlert('Failed to update primary account', 'error');
    }
  };

  // Handle bank details form submission
  const handleBankSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSavingBank(true);
      setBankError('');

      // Validate account numbers match
      if (bankDetails.accountNumber !== bankDetails.confirmAccountNumber) {
        setBankError('Account numbers do not match');
        return;
      }

      // Validate required fields
      if (!bankDetails.accountHolderName || !bankDetails.accountNumber || !bankDetails.ifscCode || !bankDetails.bankName) {
        setBankError('Please fill in all required fields');
        return;
      }

      // Prepare bank data
      const bankData = {
        accountHolderName: bankDetails.accountHolderName,
        accountNumber: bankDetails.accountNumber,
        ifscCode: bankDetails.ifscCode,
        bankName: bankDetails.bankName,
        branchName: bankDetails.branchName,
        accountType: bankDetails.accountType,
        upiId: bankDetails.upiId,
        isPrimary: bankDetails.isPrimary || bankAccounts.length === 0,
        vendorId: currentUser.uid,
        updatedAt: new Date()
      };

      // Handle UPI scanner image upload if provided
      if (bankDetails.upiScannerImage) {
        try {
          // Upload the UPI scanner image to storage
          const upiScannerURL = await uploadUpiScannerImage(bankDetails.upiScannerImage, currentUser.uid);
          bankData.upiScannerURL = upiScannerURL;
        } catch (uploadError) {
          console.error('Error uploading UPI scanner image:', uploadError);
          // Continue without the image rather than failing the entire form
          showAlert('Bank account saved, but UPI image upload failed. Please try uploading the image again.', 'warning');
        }
      } else if (isEditingBank && bankDetails.upiScannerPreview) {
        // Keep existing UPI scanner URL if no new image was uploaded
        const existingAccount = bankAccounts.find(acc => acc.id === editingBankId);
        if (existingAccount && existingAccount.upiScannerURL) {
          bankData.upiScannerURL = existingAccount.upiScannerURL;
        }
      }

      if (isAddingBank) {
        // Add new bank account
        bankData.createdAt = new Date();
        
        // If this is the first account or marked as primary, make it primary and unset others
        if (bankData.isPrimary) {
          await Promise.all(bankAccounts.map(acc => 
            setDoc(doc(db, 'vendors', currentUser.uid, 'bankAccounts', acc.id), {
              ...acc,
              isPrimary: false,
              updatedAt: new Date()
            })
          ));
        }
        
        const docRef = await addDoc(collection(db, 'vendors', currentUser.uid, 'bankAccounts'), bankData);
        setBankAccounts(prev => [...prev.map(acc => ({ ...acc, isPrimary: false })), { id: docRef.id, ...bankData }]);
        showAlert('Bank account added successfully!', 'success');
      } else {
        // Update existing bank account
        if (bankData.isPrimary) {
          await Promise.all(bankAccounts.map(acc => 
            acc.id !== editingBankId ? setDoc(doc(db, 'vendors', currentUser.uid, 'bankAccounts', acc.id), {
              ...acc,
              isPrimary: false,
              updatedAt: new Date()
            }) : Promise.resolve()
          ));
        }
        
        await setDoc(doc(db, 'vendors', currentUser.uid, 'bankAccounts', editingBankId), bankData);
        setBankAccounts(prev => prev.map(acc => 
          acc.id === editingBankId ? { id: editingBankId, ...bankData } : 
          bankData.isPrimary ? { ...acc, isPrimary: false } : acc
        ));
        showAlert('Bank account updated successfully!', 'success');
      }
      
      // Reset form state
      setIsAddingBank(false);
      setIsEditingBank(false);
      setEditingBankId(null);
      resetBankForm();
      
    } catch (error) {
      console.error('Error saving bank details:', error);
      setBankError(error.message || 'Failed to save bank details. Please try again.');
    } finally {
      setSavingBank(false);
    }
  };

  // Handle cancel edit/add
  const handleCancelBank = () => {
    setIsAddingBank(false);
    setIsEditingBank(false);
    setEditingBankId(null);
    resetBankForm();
    setBankError('');
  };

  // Mask account number for display
  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber || accountNumber.length < 4) return accountNumber;
    return '*'.repeat(accountNumber.length - 4) + accountNumber.slice(-4);
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
          <p className="text-center mt-4 text-gray-500">Loading profile...</p>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">
              Vendor Profile
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage your profile information and bank details
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white shadow-sm rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('personal')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'personal'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } transition-colors duration-200`}
              >
                <span className="mr-2">👤</span>
                Personal Information
              </button>
              <button
                onClick={() => setActiveTab('bank')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'bank'
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } transition-colors duration-200`}
              >
                <span className="mr-2">🏦</span>
                Bank Details
                {bankAccounts.length > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-600 bg-red-100 rounded-full">
                    {bankAccounts.length}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {/* Personal Information Tab */}
        {activeTab === 'personal' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Personal Information</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Update your shop details and contact information.
              </p>
            </div>

            {error && (
              <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="px-4 py-5 sm:p-6">
                <div className="grid grid-cols-1 gap-6">
                  {/* Shop Logo */}
                  <div className="flex flex-col items-center py-6">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 text-center mb-3">
                        Shop Logo
                      </label>
                      <div className="w-32 h-32 relative">
                        <FileUpload
                          accept="image/*"
                          onChange={handleLogoChange}
                          preview={formData.logoPreview}
                          defaultPreview="/assets/images/default-shop.png"
                          className="w-32 h-32 rounded-full border-2 border-gray-300 overflow-hidden cursor-pointer hover:border-red-400 transition-colors"
                          previewClassName="w-full h-full object-cover"
                          label=""
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 text-center max-w-xs">
                      Click on the image above to change your shop logo. Recommended size: 200x200 pixels.
                    </p>
                  </div>
                  
                  {/* Shop Information */}
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                      label="Shop Name"
                      id="shopName"
                      name="shopName"
                      type="text"
                      value={formData.shopName}
                      onChange={handleChange}
                      required
                    />
                    
                    <Input
                      label="Contact Person Name"
                      id="contactPersonName"
                      name="contactPersonName"
                      type="text"
                      value={formData.contactPersonName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                      label="Phone Number"
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                    
                    <Input
                      label="Email Address"
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Address
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      rows={3}
                      className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md"
                      value={formData.address}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                      Updating Profile...
                    </div>
                  ) : (
                    'Update Profile'
                  )}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Bank Details Tab */}
        {activeTab === 'bank' && (
          <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="bg-white shadow-sm rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Bank Accounts</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Manage your bank accounts for payments and settlements.
                  </p>
                </div>
                {!isAddingBank && !isEditingBank && (
                  <button
                    onClick={handleAddBank}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Bank Account
                  </button>
                )}
              </div>
            </div>

            {/* Bank Accounts List */}
            {bankAccounts.length > 0 && !isAddingBank && !isEditingBank && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <ul className="divide-y divide-gray-200">
                  {bankAccounts.map((account) => (
                    <li key={account.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <div className="flex-1">
                              <div className="flex items-center">
                                <h4 className="text-sm font-medium text-gray-900">
                                  {account.accountHolderName}
                                </h4>
                                {account.isPrimary && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Primary
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-sm text-gray-500">
                                <p>{account.bankName} • {maskAccountNumber(account.accountNumber)} • {account.ifscCode}</p>
                                <p className="capitalize">{account.accountType} Account</p>
                                {account.upiId && (
                                  <p className="flex items-center">
                                    <span className="mr-1">📱</span>
                                    UPI: {account.upiId}
                                  </p>
                                )}
                              </div>
                            </div>
                            {account.upiScannerURL && (
                              <div className="ml-4 flex flex-col items-center">
                                <div className="relative group cursor-pointer" onClick={() => {
                                  // Create a modal-like overlay
                                  const overlay = document.createElement('div');
                                  overlay.style.cssText = `
                                    position: fixed;
                                    top: 0;
                                    left: 0;
                                    width: 100%;
                                    height: 100%;
                                    background: rgba(0, 0, 0, 0.8);
                                    display: flex;
                                    justify-content: center;
                                    align-items: center;
                                    z-index: 9999;
                                    cursor: pointer;
                                  `;
                                  
                                  const container = document.createElement('div');
                                  container.style.cssText = `
                                    background: white;
                                    padding: 20px;
                                    border-radius: 12px;
                                    text-align: center;
                                    max-width: 90%;
                                    max-height: 90%;
                                  `;
                                  
                                  container.innerHTML = `
                                    <h3 style="margin: 0 0 15px 0; color: #1f2937; font-size: 18px; font-weight: 600;">UPI QR Code</h3>
                                    <p style="margin: 0 0 20px 0; color: #6b7280; font-size: 14px;">${account.accountHolderName}</p>
                                    <img src="${account.upiScannerURL}" style="max-width: 300px; max-height: 300px; border: 2px solid #e5e7eb; border-radius: 8px; display: block; margin: 0 auto;" alt="UPI QR Code" />
                                    <p style="margin: 15px 0 0 0; color: #6b7280; font-size: 12px;">Scan this QR code to make UPI payments</p>
                                    <p style="margin: 10px 0 0 0; color: #9ca3af; font-size: 11px;">Click anywhere to close</p>
                                  `;
                                  
                                  overlay.appendChild(container);
                                  document.body.appendChild(overlay);
                                  
                                  // Close on click
                                  overlay.addEventListener('click', () => {
                                    document.body.removeChild(overlay);
                                  });
                                  
                                  // Prevent clicks on the container from closing
                                  container.addEventListener('click', (e) => {
                                    e.stopPropagation();
                                  });
                                }}>
                                  <img
                                    src={account.upiScannerURL}
                                    alt="UPI QR Code"
                                    className="w-16 h-16 object-cover rounded-lg border-2 border-gray-200 hover:border-red-300 transition-colors"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 rounded-lg transition-all duration-200 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2 text-center leading-tight">Click to<br/>enlarge</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          {!account.isPrimary && (
                            <button
                              onClick={() => handleSetPrimary(account.id)}
                              className="text-sm text-red-600 hover:text-red-900 font-medium px-2 py-1"
                            >
                              Set Primary
                            </button>
                          )}
                          <button
                            onClick={() => handleEditBank(account)}
                            className="text-sm text-gray-600 hover:text-gray-900 font-medium px-2 py-1"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteBank(account.id)}
                            className="text-sm text-red-600 hover:text-red-900 font-medium px-2 py-1"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Add/Edit Bank Form */}
            {(isAddingBank || isEditingBank) && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {isAddingBank ? 'Add Bank Account' : 'Edit Bank Account'}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    {isAddingBank ? 'Add a new bank account for payments.' : 'Update your bank account information.'}
                  </p>
                </div>

                {bankError && (
                  <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">{bankError}</h3>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleBankSubmit}>
                  <div className="px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-1 gap-6">
                      {/* Account Holder Name */}
                      <div>
                        <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700">
                          Account Holder Name *
                        </label>
                        <input
                          type="text"
                          id="accountHolderName"
                          name="accountHolderName"
                          value={bankDetails.accountHolderName}
                          onChange={handleBankChange}
                          className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md"
                          placeholder="Enter account holder name"
                          required
                        />
                      </div>

                      {/* Account Number */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
                            Account Number *
                          </label>
                          <input
                            type="text"
                            id="accountNumber"
                            name="accountNumber"
                            value={bankDetails.accountNumber}
                            onChange={handleBankChange}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md font-mono"
                            placeholder="Enter account number"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="confirmAccountNumber" className="block text-sm font-medium text-gray-700">
                            Confirm Account Number *
                          </label>
                          <input
                            type="text"
                            id="confirmAccountNumber"
                            name="confirmAccountNumber"
                            value={bankDetails.confirmAccountNumber}
                            onChange={handleBankChange}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md font-mono"
                            placeholder="Re-enter account number"
                            required
                          />
                        </div>
                      </div>

                      {/* IFSC Code and Account Type */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">
                            IFSC Code *
                          </label>
                          <input
                            type="text"
                            id="ifscCode"
                            name="ifscCode"
                            value={bankDetails.ifscCode}
                            onChange={handleBankChange}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md font-mono uppercase"
                            placeholder="Enter IFSC code"
                            maxLength={11}
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="accountType" className="block text-sm font-medium text-gray-700">
                            Account Type
                          </label>
                          <select
                            id="accountType"
                            name="accountType"
                            value={bankDetails.accountType}
                            onChange={handleBankChange}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md"
                          >
                            <option value="savings">Savings</option>
                            <option value="current">Current</option>
                            <option value="cc">CC/OD</option>
                          </select>
                        </div>
                      </div>

                      {/* Bank Name and Branch */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
                            Bank Name *
                          </label>
                          <input
                            type="text"
                            id="bankName"
                            name="bankName"
                            value={bankDetails.bankName}
                            onChange={handleBankChange}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md"
                            placeholder="Enter bank name"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="branchName" className="block text-sm font-medium text-gray-700">
                            Branch Name
                          </label>
                          <input
                            type="text"
                            id="branchName"
                            name="branchName"
                            value={bankDetails.branchName}
                            onChange={handleBankChange}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md"
                            placeholder="Enter branch name"
                          />
                        </div>
                      </div>

                      {/* UPI Details Section */}
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                          <span className="mr-2">📱</span>
                          UPI Payment Details (Optional)
                        </h4>

                        {/* UPI ID */}
                        <div className="mb-4">
                          <label htmlFor="upiId" className="block text-sm font-medium text-gray-700">
                            UPI ID
                          </label>
                          <input
                            type="text"
                            id="upiId"
                            name="upiId"
                            value={bankDetails.upiId}
                            onChange={handleBankChange}
                            className="mt-1 block w-full shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm border border-gray-300 rounded-md font-mono"
                            placeholder="yourname@upi"
                          />
                          <p className="mt-1 text-sm text-gray-500">
                            For quick UPI payments and settlements
                          </p>
                        </div>

                        {/* UPI Scanner Image */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            UPI QR Code Scanner
                          </label>
                          <div className="flex items-start space-x-4">
                            <div className="w-32 h-32 flex-shrink-0">
                              <FileUpload
                                accept="image/*"
                                onChange={handleUpiScannerChange}
                                preview={bankDetails.upiScannerPreview}
                                defaultPreview={null}
                                className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg overflow-hidden cursor-pointer hover:border-red-400 transition-colors flex items-center justify-center"
                                previewClassName="w-full h-full object-cover"
                                label=""
                              />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-600 mb-2">
                                Upload your UPI QR code image for customers to scan and make payments directly.
                              </p>
                              <ul className="text-xs text-gray-500 space-y-1">
                                <li>• Recommended size: 300x300 pixels</li>
                                <li>• Supported formats: JPG, PNG</li>
                                <li>• Clear, high-contrast QR code for easy scanning</li>
                                <li>• This will be displayed to customers during checkout</li>
                              </ul>
                            </div>
                          </div>
                          {!bankDetails.upiScannerPreview && (
                            <div className="mt-2 text-center">
                              <button
                                type="button"
                                onClick={() => document.querySelector('input[type="file"][accept="image/*"]:last-of-type').click()}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Upload QR Code
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Primary Account Checkbox */}
                      <div className="flex items-center">
                        <input
                          id="isPrimary"
                          name="isPrimary"
                          type="checkbox"
                          checked={bankDetails.isPrimary}
                          onChange={handleBankChange}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-900">
                          Set as primary account
                        </label>
                      </div>

                      {/* Security Notice */}
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">Security Information</h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <p>
                                Your bank details and UPI information are encrypted and stored securely. We use this information only for payment settlements and will never share it with third parties.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-between items-center">
                    <button
                      type="button"
                      onClick={handleCancelBank}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Cancel
                    </button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={savingBank}
                    >
                      {savingBank ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                          </svg>
                          {isAddingBank ? 'Adding...' : 'Updating...'}
                        </div>
                      ) : (
                        isAddingBank ? 'Add Bank Account' : 'Update Bank Account'
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Empty State */}
            {bankAccounts.length === 0 && !isAddingBank && (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No bank accounts</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by adding your first bank account.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={handleAddBank}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Bank Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;